import AbstractTrackingService from '@ve/services/analytics/yandexAnalytics/abstractTrackingService';
import { IBackboneProduct } from '@ve/types/backboneModels/Domain/ObjectValue/BackboneProduct';
import { OperatorId } from '@entities/operator';
import HotelPagePriceJumpTrackingService from './HotelPagePriceJumpTrackingService';
import {
    IParamsEmptySearchAnalytic,
    IParamsForBackBtn,
    IParamsForHotelPageGalleryAnalytic,
    IParamsHotelDescription,
} from './types';

export default class HotelTrackingService extends AbstractTrackingService {
    private static _instance: HotelTrackingService;

    protected trackingServicePage = 'hotel';

    public priceJumpTracking!: HotelPagePriceJumpTrackingService;

    constructor() {
        super();
        if (HotelTrackingService._instance) {
            return HotelTrackingService._instance;
        }
        HotelTrackingService._instance = this;
    }

    setPriceJumpTrackingService(hotelPagePriceJumpTrackingService: HotelPagePriceJumpTrackingService): void {
        if (this.priceJumpTracking) {
            return;
        }

        this.priceJumpTracking = hotelPagePriceJumpTrackingService;
    }

    trackHotelJumpPrice(hotelMinPrice: number | undefined, serpMinPrice: number | undefined): void {
        if (!serpMinPrice || !hotelMinPrice) return;

        const {
            priceJumpType,
            shouldTrackingPriceJump,
        } = this.priceJumpTracking.getTrackHotelJumpPriceData(hotelMinPrice, serpMinPrice);

        if (priceJumpType === 'decrease' && shouldTrackingPriceJump) {
            this.setTrackingGoal('serp_hotel_page_minus_500rub', 'click');
            return;
        }
        if (priceJumpType === 'increase' && shouldTrackingPriceJump) {
            this.setTrackingGoal('serp_hotel_page_500rub', 'click');
        }
    }

    trackHotelJumpPriceFromCache(hotelMinPrice: number | undefined, serpMinPrice: number | undefined): void {
        if (!serpMinPrice || !hotelMinPrice) return;

        const {
            shouldTrackPriceJump,
            priceJumpType,
            priceJumpValue,
            goalParams,
        } = this.priceJumpTracking.trackJumpPrice(
            serpMinPrice,
            hotelMinPrice,
            'search_hotel',
        );

        if (!shouldTrackPriceJump) {
            return;
        }

        this.setTrackingGoal(`search_hotel_${priceJumpType}_${priceJumpValue}`, 'other', goalParams);
    }

    trackHotelJumpBetweenCacheAndSearch(fullSearchPrice: number | undefined): void {
        if (!fullSearchPrice) return;

        const {
            shouldTrackPriceJump,
            priceJumpType,
            priceJumpValue,
            goalParams,
        } = this.priceJumpTracking.trackJumpHotelPageBetweenCacheAndSearch(fullSearchPrice);

        if (!shouldTrackPriceJump) {
            return;
        }

        this.setTrackingGoal(`hotel_cache_hotel_actual_${priceJumpType}_${priceJumpValue}`, 'other', goalParams);
    }

    searchFormRegionClick(): void {
        this.setTrackingGoal('hotelpage_searchform_region_click', 'click');
    }

    datesTabsClick(price: boolean): void {
        this.setTrackingGoal('hotelpage_dates_tabs_click', 'click', { price });
    }

    headPriceButtonClick(): void {
        this.setTrackingGoal('hotelpage_head_pricebutton_click', 'click');
    }

    allReviewsClick(): void {
        this.setTrackingGoal('hotelpage_allReviewsLink', 'click');
    }

    viewMapLinkClick(): void {
        this.setTrackingGoal('hotelpage_viewMapLink', 'click');
    }

    hotelDescriptionClick(): void {
        this.setTrackingGoal('hotelpage_hotelDetails_about-more', 'click');
    }

    mealsFilterClick(): void {
        this.setTrackingGoal('hotelpage_meals-filter_click', 'click');
    }

    operatorsFilterClick(): void {
        this.setTrackingGoal('hotelpage_operators_filter_click', 'click');
    }

    hotelPageGalleryClick(hotelPageGalleryParams: IParamsForHotelPageGalleryAnalytic): void {
        this.setTrackingGoal('hotelpage_gallery', 'click', hotelPageGalleryParams);
    }

    hotelSearchFormClick(): void {
        this.setTrackingGoal('searchform_click', 'click');
    }

    hotelBtnSearchClick(param: boolean): void {
        this.setTrackingGoal('btn_search_click', 'click', { isValueChanged: param });
    }

    public bannerThClick(isUserAuth: boolean): void {
        this.bannerThTracking(isUserAuth, 'hotels');
    }

    hotelBackBtnClick(param: IParamsForBackBtn): void {
        this.setTrackingGoal('hotelpage_backbutton_click', 'click', param);
    }

    trackingHotelSearch(params: IParamsEmptySearchAnalytic): void {
        this.setTrackingGoal('hotelpage_search_result', 'other', params);
    }

    clickTourButtonWithConfirmation(typeConfirmation: string): void {
        this.setTrackingGoal('hotelPage_tours_on_request', 'click', { typeConfirmation });
    }

    trackingHotelDescription(params: IParamsHotelDescription): void {
        this.setTrackingGoal('hotelpage_version', 'other', params);
    }

    trackReviewsMoreClick(): void {
        this.setTrackingGoal('hotel-page-click-more-reviews', 'click');
    }
    trackReviewsSourceClick(source: string): void {
        this.setTrackingGoal('hotel-page-click-source-reviews', 'click', { source });
    }

    trackClickExlusive(): void {
        this.setTrackingGoal('hotel-page_filter_exlusive', 'click');
    }

    trackBeforeSearchFinishClick(params: { products?: IBackboneProduct[], loadingState?: 'finished' }): void {
        const { products, loadingState } = params;
        if (loadingState === 'finished' || !products) {
            return;
        }
        const isXtravel = (product: IBackboneProduct) => OperatorId.Travelata.includes(product.get('product').get('operator').id);
        const hasXtravelTours = products.some((product) => product.get('type') === 'TourProduct' && isXtravel(product));
        const hasXtravelHotels = products.some((product) => product.get('type') === 'HotelOfferProduct' && isXtravel(product));
        this.setTrackingGoal('hotel_page_click_product_before_search_finish', 'click', { hasXtravelTours, hasXtravelHotels });
    }

    trackAvailableTravelata() {
        this.setTrackingGoal('hotel_page_reviews_available_travelata', 'other');
    }
    trackAvailableTophotels() {
        this.setTrackingGoal('hotel_page_reviews_available_tophotels', 'other');
    }
    trackAvailableTripadvisor() {
        this.setTrackingGoal('hotel_page_reviews_available_tripadvisor', 'other');
    }
    trackShowReviewsClick() {
        this.setTrackingGoal('hotel_page_show_reviews_click', 'click');
    }
    trackShowReviewsSource(params: { reviewsSource: string }) {
        this.setTrackingGoal('hotel_page_reviews_show', 'other', params);
    }
    trackReviewsTravelataClick() {
        this.setTrackingGoal('hotel_page_reviews_travelata_click', 'click');
    }
    trackReviewsTopHotelsClick() {
        this.setTrackingGoal('hotel_page_reviews_tophotels_click', 'click');
    }
    trackReviewsTripadvisorClick() {
        this.setTrackingGoal('hotel_page_reviews_tripadvisor_click', 'click');
    }
    trackReviewsTravelataMoreClick() {
        this.setTrackingGoal('hotel_page_reviews_travelata_ten_more_click', 'click');
    }
    trackReviewsTopHotelsMoreClick() {
        this.setTrackingGoal('hotel_page_reviews_tophotels_ten_more_click', 'click');
    }
    trackReviewsTripadvisorMoreClick() {
        this.setTrackingGoal('hotel_page_reviews_tripadvisor_ten_more_click', 'click');
    }
    trackTravelataShowAllClick() {
        this.setTrackingGoal('hotel_page_reviews_travelata_show_all_click', 'click');
    }
    trackTravelataNewReviewClick() {
        this.setTrackingGoal('hotel_page_reviews_travelata_new_review_click', 'click');
    }
    trackReviewsTravelataNewReviewSuccess() {
        this.setTrackingGoal('hotel_page_reviews_travelata_new_review_success', 'other');
    }
    
    trackFilterHotelOfferShow() {
        this.setTrackingGoal('HotelPageFilterShow', 'other');
    }

    trackClickFilterHotelOffer() {
        this.setTrackingGoal('HotelPageFilterHotelClick', 'click');
    }
}